import { content } from "../../../config/content.js";
import {
  buildTeamAvatarRef,
  buildTeamAvatarSprite,
  experienceLabel,
} from "../TeamAvatar.js";

const SCOPE = "squad";
const SLOT_LABELS = ["Full-stack", "Backend", "Mobile", "QA"];
const RAMP = ["Day 1 · access", "Week 1 · first PR", "Week 2 · owns a lane"];

/* Candidates matched against the brief, in the order they land on the bench. */
const PICKS = ["Ayesha Khan", "Hamza Riaz", "Zainab Ali", "Hina Rizvi"];

export function createStaffAugmentationStage() {
  const members = content.team.members;
  const picks = PICKS.map((name) => members.findIndex((m) => m.name === name)).filter((i) => i >= 0);

  const wrap = document.createElement("div");
  wrap.className = "services-stage services-stage--staff reveal";
  wrap.style.setProperty("--delay", "0.18s");
  wrap.setAttribute("aria-hidden", "true");

  wrap.innerHTML = `
    ${buildTeamAvatarSprite(members, SCOPE)}
    <div class="services-stage__glow"></div>
    <div class="services-stage__orb services-stage__orb--a"></div>
    <div class="services-stage__orb services-stage__orb--b"></div>
    <div class="services-stage__orb services-stage__orb--c"></div>

    <div class="services-stage__window services-stage__panel">
      <div class="services-stage__chrome">
        <span class="services-stage__dot services-stage__dot--r"></span>
        <span class="services-stage__dot services-stage__dot--y"></span>
        <span class="services-stage__dot services-stage__dot--g"></span>
        <span class="services-stage__path">squad.studio / build-your-bench</span>
        <span class="services-stage__live" data-live><span></span>matching</span>
      </div>

      <div class="squad-stage">
        <div class="squad-stage__brief">
          <span class="squad-stage__eyebrow">Role brief</span>
          <strong>Product squad · 4 seats</strong>
          <small>React · Node · Flutter · Playwright · CET overlap · start in 5 days</small>
        </div>

        <div class="squad-stage__grid">
          <div class="squad-stage__candidates" data-candidates>
            ${picks
              .map((index, order) => {
                const m = members[index];
                return `
                <div class="squad-stage__candidate" data-candidate="${order}">
                  <span class="squad-stage__avatar">${buildTeamAvatarRef(m, index, SCOPE, "square")}</span>
                  <span class="squad-stage__meta">
                    <strong>${m.name}</strong>
                    <small>${m.role} · ${experienceLabel(m).replace(" experience", "")}</small>
                  </span>
                  <span class="squad-stage__fit"><b>${94 + ((order * 3) % 5)}</b>fit</span>
                </div>
              `;
              })
              .join("")}
          </div>

          <div class="squad-stage__bench">
            <span class="squad-stage__eyebrow">Your squad <em data-filled>0 / 4</em></span>
            <div class="squad-stage__slots" data-slots>
              ${SLOT_LABELS.map(
                (label, i) => `
                <div class="squad-stage__slot" data-slot="${i}">
                  <span class="squad-stage__slot-avatar"></span>
                  <small>${label}</small>
                </div>
              `
              ).join("")}
            </div>
            <div class="squad-stage__ramp">
              ${RAMP.map((r, i) => `<span class="${i === 0 ? "is-on" : ""}"><i></i>${r}</span>`).join("")}
            </div>
          </div>
        </div>
      </div>

      <div class="services-stage__footer">
        <span>AI-vetted</span>
        <span>Your tools</span>
        <span class="services-stage__pulse" data-pulse>scoring fit</span>
      </div>
    </div>
  `;

  bindSquadRuntime(wrap, members, picks);
  return wrap;
}

function bindSquadRuntime(root, members, picks) {
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const candidates = [...root.querySelectorAll("[data-candidate]")];
  const slots = [...root.querySelectorAll("[data-slot]")];
  const filled = root.querySelector("[data-filled]");
  const ramp = [...root.querySelectorAll(".squad-stage__ramp span")];
  const live = root.querySelector("[data-live]");
  const pulse = root.querySelector("[data-pulse]");

  const paint = (count) => {
    candidates.forEach((c, i) => {
      c.classList.toggle("is-matched", i < count);
      c.classList.toggle("is-scoring", i === count);
    });
    slots.forEach((slot, i) => {
      const on = i < count;
      slot.classList.toggle("is-filled", on);
      const holder = slot.querySelector(".squad-stage__slot-avatar");
      if (on && !holder.firstElementChild) {
        holder.innerHTML = buildTeamAvatarRef(members[picks[i]], picks[i], "squad", "square");
      }
      if (!on) holder.innerHTML = "";
    });
    if (filled) filled.textContent = `${count} / ${slots.length}`;
    const done = count >= slots.length;
    ramp.forEach((r, i) => r.classList.toggle("is-on", done ? true : i === 0));
    if (live) live.innerHTML = `<span></span>${done ? "squad ready" : "matching"}`;
    if (pulse) pulse.textContent = done ? "kickoff booked" : "scoring fit";
  };

  if (reduced) {
    paint(slots.length);
    return;
  }

  let count = 0;
  let timer = null;
  paint(count);

  const tick = () => {
    count = (count + 1) % (slots.length + 2); // hold the full squad for a beat
    paint(Math.min(count, slots.length));
  };

  const start = () => {
    if (timer) return;
    timer = window.setInterval(tick, 1600);
  };
  const stop = () => {
    if (!timer) return;
    clearInterval(timer);
    timer = null;
  };

  start();
  new IntersectionObserver(([entry]) => (entry.isIntersecting ? start() : stop()), {
    threshold: 0.2,
  }).observe(root);
}
